import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const DEACTIVATE_DELAY = 280;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
      signal: this.#abortController.signal,
    });

    onDocumentLoaded(this.#preloadImages);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu panel (only present when using <overflow-list>)
   */
  get overflowMenu() {
    const overflowList = this.refs.overflowMenu;
    if (!overflowList) return null;
    return /** @type {HTMLElement | null} */ (overflowList.shadowRoot?.querySelector('[part="overflow"]') ?? null);
  }

  /**
   * True when the pointer is over the open mega-menu panel (not a sibling nav item).
   * @returns {boolean}
   */
  #isPointerOverOpenSubmenu() {
    const item = this.#state.activeItem;
    if (!item) return false;

    const submenu = findSubmenu(item);
    if (submenu?.matches(':hover')) return true;

    const overflowPanel = this.overflowMenu;
    if (overflowPanel?.matches(':hover')) return true;

    return false;
  }

  /**
   * Activate the selected menu item immediately (sync so a later pointerleave
   * on the previous item cannot cancel the new item's open).
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#activateHandler(event);
  };

  /**
   * Activate the selected menu item
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    if (!item) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    let submenu = findSubmenu(item);

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    // Neighbor without a mega menu: do not cancel a pending close from leaving
    // the previous item (Catalog must not keep Sections' curtain open).
    if (!submenu) {
      this.dataset.overflowExpanded = 'false';
      if (this.#state.activeItem) {
        this.#debouncedDeactivate(this.#state.activeItem);
      }
      return;
    }

    this.#debouncedDeactivate.cancel();

    if (item === this.#state.activeItem) return;

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
      previouslyActiveItem.removeAttribute('data-animating');
      const previousSubmenu = findSubmenu(previouslyActiveItem);
      if (previousSubmenu) {
        delete previousSubmenu.dataset.active;
      }
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';
    submenu.dataset.active = '';

    this.#syncSubmenuMetrics(submenu);
  };

  /**
   * Measure after layout so the navy panel height is never 0 on first open.
   * @param {HTMLElement | null} submenu
   */
  #syncSubmenuMetrics = (submenu) => {
    const apply = () => {
      const height = submenu ? Math.max(submenu.scrollHeight, submenu.offsetHeight) : 0;
      this.style.setProperty('--submenu-height', `${height}px`);
      this.style.setProperty('--submenu-opacity', height > 0 ? '1' : '0');
    };

    apply();
    requestAnimationFrame(apply);
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    // Deactivate this specific item so a later timer cannot close a newly opened neighbor.
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate(item);
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.#isPointerOverOpenSubmenu()) return;

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    if (submenu) {
      delete submenu.dataset.active;
    }

    setTimeout(() => {
      item.removeAttribute('data-animating');
    }, Math.max(0, this.animationDelay - 150));
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
